<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useFloating } from '@/composables/useFloating.js'
import { useMeta } from '@/stores/meta.js'
import { useSession } from '@/stores/session.js'
import { fold } from '@addictivegroup/addictive-offline'
import TagChip from '@/components/ui/TagChip.vue'

/**
 * Tags (D-45): chosen from approved ones, or typed as new (pending approval) where the
 * category allows it. v-model: list of uuids or {name, category_uuid} for new ones — the
 * shape Core's TagService takes. `display` is what the record currently shows.
 */
const props = defineProps({ modelValue: { type: Array, default: () => [] }, display: { type: Array, default: () => [] }, disabled: Boolean })
const emit = defineEmits(['update:modelValue'])
const meta = useMeta()
const session = useSession()
const query = ref('')
const open = ref(false)

const known = computed(() => meta.data?.tags || [])
const categories = computed(() => meta.data?.tag_categories || [])
const selected = computed(() => props.modelValue.map((item) => {
  if (typeof item === 'string') {
    const tag = known.value.find((t) => t.uuid === item) || props.display.find((t) => t.uuid === item)
    return { key: item, name: tag?.name || '…', pending: tag?.status === 'pending', category: meta.tagCategory(tag?.category_uuid || tag?.category?.uuid) }
  }
  return { key: `new:${item.name}`, name: item.name, pending: true, category: meta.tagCategory(item.category_uuid) }
}))
const suggestions = computed(() => {
  const q = fold(query.value)
  return known.value.filter((t) => t.status === 'approved' && !props.modelValue.includes(t.uuid) && (!q || fold(t.name).includes(q))).slice(0, 10)
})
const canCreate = computed(() => session.has('tag:create') && query.value.trim().length >= 2 && !known.value.some((t) => fold(t.name) === fold(query.value.trim())))
const creatableCategories = computed(() => categories.value.filter((c) => c.allows_user_tags || session.has('tag:manage')))

function add(uuid) {
  emit('update:modelValue', [...props.modelValue, uuid])
  query.value = ''
}
function create(category) {
  emit('update:modelValue', [...props.modelValue, { name: query.value.trim(), category_uuid: category.uuid }])
  query.value = ''
  open.value = false
}
// The suggestions float over the page by the field — above it when there is more room there.
const { anchor, floating, style, show, close: closeList, place } = useFloating({ fit: () => true })
const listed = computed(() => open.value && (suggestions.value.length > 0 || canCreate.value))
watch(listed, (on) => (on ? show() : closeList()))
watch(suggestions, () => listed.value && nextTick(place))
function close() {
  setTimeout(() => (open.value = false), 150)
}
function remove(i) {
  emit('update:modelValue', props.modelValue.filter((_, j) => j !== i))
}
</script>

<template>
  <div ref="anchor" class="relative">
    <div class="input flex min-h-[2.4rem] flex-wrap items-center gap-1.5">
      <TagChip v-for="(tag, i) in selected" :key="tag.key" :name="tag.name" :category="tag.category" :pending="tag.pending" :removable="!disabled" @remove="remove(i)" />
      <input v-if="!disabled" v-model="query" class="min-w-24 flex-1 bg-transparent text-sm outline-none" :placeholder="selected.length ? '' : $t('actions.tag')" @focus="open = true" @blur="close" />
    </div>
    <Teleport to="body">
    <div v-if="listed" ref="floating" :style="style" class="surface fixed max-h-64 overflow-y-auto rounded-xl border border-default py-1 shadow-xl" data-testid="tags-list">
      <button v-for="t in suggestions" :key="t.uuid" type="button" class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-[var(--surface-3)]" @mousedown.prevent="add(t.uuid)">
        <TagChip :name="t.name" :category-uuid="t.category_uuid" />
      </button>
      <template v-if="canCreate">
        <div class="my-1 border-t border-default"></div>
        <button v-for="c in creatableCategories" :key="c.uuid" type="button" class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-[var(--surface-3)]" @mousedown.prevent="create(c)">
          <i class="fa-solid fa-plus text-xs text-faint" aria-hidden="true"></i> „{{ query.trim() }}” → {{ meta.localized(c.name) }}
        </button>
      </template>
    </div>
    </Teleport>
  </div>
</template>
